/**
 * The guidelines and the policy a workspace attaches to its plans - the
 * stored copies the Guidelines & policy tab edits against.
 *
 * Under TanStack Query, the way `useDishScope` keeps the other My brand
 * setting: one entry per document for the workspace, read by whichever
 * screen asks, and written through on a publish or a reset so the tab is on
 * the stored version the moment the server answers. Two entries rather than
 * one because they are two endpoints that fail on their own: a policy that
 * would not load is no reason to leave the guidelines unread.
 *
 * The text a workspace starts on is the server's too. `GET` answers with
 * the system default until the workspace has published, and `reset/` puts
 * it back - there is no copy of it here, so the two can never drift apart.
 */

import { useCallback, useEffect, useMemo } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  fetchPolicyDocument,
  publishPolicyDocument,
  resetPolicyDocument,
} from "../services/policyService";
import type {
  PolicyDocument,
  PolicyDocumentId,
  StoredPolicyDocuments,
} from "../types/brand.types";

export type BrandPoliciesStatus = "loading" | "ready" | "failed";

export interface BrandPoliciesState {
  /**
   * Both documents as stored, once both have been read. `null` until then -
   * the tab has nothing to open on before the server has answered, and a
   * blank box that fills itself a moment later reads as the screen
   * overwriting what was typed. Each document in it is a fresh object only
   * when a read or a write of that document landed, so a screen seeding
   * from it can tell that from a re-render.
   */
  stored: StoredPolicyDocuments | null;
  status: BrandPoliciesStatus;
  /** Set only when `status` is `failed` - what the screen shows. */
  error?: string;
  /** Runs the reads again. For a Try again button. */
  reload: () => void;
  /**
   * Publishes a new version of one document from the body as written. The
   * policy's clauses go with it - the ones its body still names - since a
   * publish takes the whole list every time. Answers with the version as
   * stored, which is what the box has to show: the server keeps only what
   * is on its allow-list, and the editor must not go on showing what it
   * took off.
   */
  publish: (id: PolicyDocumentId, body: string) => Promise<PolicyDocument>;
  /** Puts the system default back on one document, and answers with it. */
  reset: (id: PolicyDocumentId) => Promise<PolicyDocument>;
}

export const BRAND_POLICIES_QUERY_KEY = ["brand", "policies"] as const;
export const policyQueryKey = (id: PolicyDocumentId) =>
  [...BRAND_POLICIES_QUERY_KEY, id] as const;

const DOCUMENT_IDS: PolicyDocumentId[] = ["guidelines", "policy"];

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

export function useBrandPolicies(): BrandPoliciesState {
  const getToken = useTokenGetter();
  const queryClient = useQueryClient();

  const [guidelines, policy] = useQueries({
    queries: DOCUMENT_IDS.map((id) => ({
      queryKey: policyQueryKey(id),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        fetchPolicyDocument(getToken, id, signal),
    })),
  });

  // Folded on the two `data` references rather than on every render: each
  // is the same object until a read or a write lands, so `stored` is a new
  // object only then.
  const stored = useMemo<StoredPolicyDocuments | null>(
    () =>
      guidelines.data && policy.data
        ? { guidelines: guidelines.data, policy: policy.data }
        : null,
    [guidelines.data, policy.data]
  );

  const failure = [guidelines, policy].find((query) => query.isError);
  const status: BrandPoliciesStatus = failure
    ? "failed"
    : stored
      ? "ready"
      : "loading";

  useEffect(() => {
    if (failure) {
      console.error("Guidelines & policy failed to load:", failure.error);
    }
  }, [failure]);

  const reload = useCallback(() => {
    void guidelines.refetch();
    void policy.refetch();
  }, [guidelines, policy]);

  const publish = useCallback(
    async (id: PolicyDocumentId, body: string) => {
      // The clauses as last read, for the policy: a publish resends the
      // whole list, and the service keeps the ones the body still names.
      const current = queryClient.getQueryData<PolicyDocument>(policyQueryKey(id));
      const stored = await publishPolicyDocument(
        getToken,
        id,
        body,
        current?.clauses ?? []
      );
      queryClient.setQueryData<PolicyDocument>(policyQueryKey(id), stored);
      return stored;
    },
    [getToken, queryClient]
  );

  const reset = useCallback(
    async (id: PolicyDocumentId) => {
      const stored = await resetPolicyDocument(getToken, id);
      queryClient.setQueryData<PolicyDocument>(policyQueryKey(id), stored);
      return stored;
    },
    [getToken, queryClient]
  );

  return {
    stored,
    status,
    error: failure ? failureMessage(failure.error) : undefined,
    reload,
    publish,
    reset,
  };
}

export default useBrandPolicies;
